/**
 * LegalPageTemplate — structural shell for the five compliance pages.
 *
 * All five pages (Terms, Privacy, Biometric, AI Notice, Memorial Policy)
 * use this template. Final copy is injected via the `body` render-prop; the
 * template handles the eyebrow / title / version / navigation and the
 * consistent cross-links between documents.
 */
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import BrandMark from "@/components/BrandMark";
import { useLocale } from "@/lib/locale";
import { ChevronLeft } from "lucide-react";

// Kept in a single place so every page can cross-link to the others.
const LEGAL_NAV = [
    { slug: "terms",     labelKey: "legal.nav_terms",     path: "/legal/terms" },
    { slug: "privacy",   labelKey: "legal.nav_privacy",   path: "/legal/privacy" },
    { slug: "biometric", labelKey: "legal.nav_biometric", path: "/legal/biometric" },
    { slug: "ai_notice", labelKey: "legal.nav_ai_notice", path: "/legal/ai-notice" },
    { slug: "memorial",  labelKey: "legal.nav_memorial",  path: "/legal/memorial-policy" },
];

export default function LegalPageTemplate({ docType, title, eyebrow, version, children, testid }) {
    const { t } = useLocale();
    return (
        <div className="grain min-h-screen candle-halo">
            <Header minimal />
            <main className="max-w-3xl mx-auto px-6 md:px-12 pt-12 pb-24" data-testid={testid || `legal-${docType}`}>
                <Link to="/" className="inline-flex items-center gap-2 text-ink-textMid hover:text-ink-textHigh text-sm mb-8 transition-colors" data-testid={`legal-${docType}-back`}>
                    <ChevronLeft size={14} /> {t("legal.back")}
                </Link>

                <div className="flex items-center gap-4 mb-10">
                    <BrandMark size={48} breathing={false} />
                    <div>
                        <p className="label-eyebrow text-ink-gold">{eyebrow}</p>
                        <h1 className="h-serif text-4xl md:text-5xl mt-1">{title}</h1>
                    </div>
                </div>

                <div className="flex items-center gap-4 text-xs text-ink-textMid mb-10 border-b border-ink-border pb-6">
                    <span>{t("legal.version_label")}: <span className="text-ink-textHigh">{version}</span></span>
                </div>

                <article className="prose-legal space-y-6 text-ink-textMid leading-relaxed">
                    {children || (
                        <p className="italic" data-testid={`legal-${docType}-placeholder`}>
                            {t("legal.placeholder")}
                        </p>
                    )}
                </article>

                <nav className="mt-16 pt-8 border-t border-ink-border grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="legal-cross-nav">
                    {LEGAL_NAV.filter((n) => n.slug !== docType).map((n) => (
                        <Link
                            key={n.slug}
                            to={n.path}
                            className="card-paper text-sm hover:!border-ink-gold transition-colors"
                            data-testid={`legal-nav-${n.slug}`}
                        >
                            <p className="font-serif text-ink-textHigh">{t(n.labelKey)}</p>
                        </Link>
                    ))}
                </nav>
            </main>
        </div>
    );
}
